import React, { useEffect, useRef, useState } from 'react'
import cls from 'classnames'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import CloseIcon from './Icons/Close'
import Tooltip from './Tooltip'

const Chip = ({
  size = 'large',
  className,
  checked,
  label,
  onClose,
  onChange,
  checkedStyle = 'selected',
  disabled,
  prefixIcon,
  ignoreTooltip,
  noMaxWidth,
  id,
  onMouseDown,
  maxWidth,
  style,
  forwardedRef,
}) => {
  const [isLabelOverflowed, setIsLabelOverflow] = useState(false)
  const labelRef = useRef()

  useEffect(() => {
    setIsLabelOverflow(labelRef.current.scrollWidth > labelRef.current.clientWidth)
  }, [])

  return (
    <div
      className={className}
      onClick={onChange}
      ref={forwardedRef}
      onMouseDown={onMouseDown}
      style={style}
      noMaxWidth={noMaxWidth}
    >
      <div
        className={cls('chip-container', {
          pointer: !onClose,
          'small-size': size === 'small',
          'large-size': size === 'large',
          unselected: !checked && !disabled,
          selected: checked && !disabled && checkedStyle === 'selected',
          active: checked && !disabled && checkedStyle === 'active',
          disabled,
        })}
      >
        {prefixIcon && <div className="chip-icon">{prefixIcon}</div>}
        <Tooltip
          text={isLabelOverflowed ? label : undefined}
          align={{ offset: [0, -10] }}
          ignoreTooltip={ignoreTooltip}
        >
          <div
            className="chip-label"
            ref={labelRef}
            style={maxWidth ? { 'max-width': maxWidth } : undefined}
            id={`chip-label-${id || ''}`}
          >
            {label}
          </div>
        </Tooltip>
        {!!onClose && (
          <div
            id={`chip-label-remove-${id || ''}`}
            className="chip-icon pointer"
            onClick={(e) => {
              e.stopPropagation()
              onClose()
            }}
          >
            <CloseIcon />
          </div>
        )}
      </div>
    </div>
  )
}

Chip.propTypes = {
  size: PropTypes.oneOf(['small', 'large']),
  className: PropTypes.string,
  checked: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  onClose: PropTypes.func,
  onChange: PropTypes.func,
  checkedStyle: PropTypes.oneOf(['selected', 'active']),
  disabled: PropTypes.bool,
  prefixIcon: PropTypes.element,
  onMouseDown: PropTypes.func,
  maxWidth: PropTypes.string,
}

export default styled(Chip)`
  display: inline-flex;

  .chip-container {
    box-sizing: content-box;
    display: flex;
    gap: 3px;
    border-radius: 100px;
    font-size: 12px;
    font-weight: 400;
    font-family: Figtree, sans-serif !important;
    justify-content: center;
    align-items: center;
    cursor: default;

    :hover:not(.disabled):not(.selected) {
      background-color: ${({ theme }) => theme.ds_bg_paleBlue};
      color: ${({ theme }) => theme.ds_buttonHover};
      text-shadow: 0 0 0.7px;

      svg > path {
        fill: ${({ theme }) => theme.ds_buttonHover};
      }
    }

    .chip-label {
      max-width: ${({ noMaxWidth }) => (noMaxWidth ? 'none' : '110px')};
      text-align: center;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .chip-icon {
      display: inline-flex;

      svg,
      img {
        width: 16px;
        height: 16px;
      }
    }
  }

  .small-size {
    height: 16px;
    padding: 3px 12px;
  }

  .large-size {
    height: 18px;
    padding: 4px 12px;
  }

  .selected {
    background-color: ${({ theme }) => theme.ds_bg_paleBlue};
    color: ${({ theme }) => theme.ds_gray_1};

    svg > path {
      fill: ${({ theme }) => theme.ds_gray_1};
    }
  }

  .active {
    background-color: ${({ theme }) => theme.ds_bg_paleBlue};
    color: ${({ theme }) => theme.primary};
    text-shadow: 0 0 0.7px;

    svg > path {
      fill: ${({ theme }) => theme.primary};
    }
  }

  .unselected {
    background-color: ${({ theme }) => theme.ds_gray_8};
    color: ${({ theme }) => theme.ds_gray_2};

    svg > path {
      fill: ${({ theme }) => theme.ds_gray_2};
    }
  }

  .disabled {
    background-color: ${({ theme }) => theme.ds_gray_7};
    color: ${({ theme }) => theme.ds_gray_5};

    svg > path {
      fill: ${({ theme }) => theme.ds_gray_5};
    }
  }

  .pointer:not(.selected) {
    cursor: pointer;
  }
`
